import {
  DEFAULT_SORTING_CRITERIA,
  DEFAULT_SORT_ORDER_OPTIONS,
  FloorplanViewMode,
} from '@/modules/floorplanAdmin/sortingMenu/constants';
import { LayerVisibilityMode } from '@/modules/common/types/layers';
import { Theme, Unit } from '@modules/common/types/general';
import {
  DEFAULT_BOXES_ZOOM_THRESHOLD,
  DEFAULT_BUBBLES_ZOOM_THRESHOLD,
  DEFAULT_TRACKS_ZOOM_THRESHOLD,
  DEFAULT_VEHICLE_LAYOUTS_ZOOM_THRESHOLD,
} from '../lod/constants';

const AUTO: LayerVisibilityMode = 'auto';
const FORCE_SHOW: LayerVisibilityMode = 'force_show';
const FORCE_HIDE: LayerVisibilityMode = 'force_hide';

export enum UserPreferenceName {
  // General
  GENERAL_THEME = 'general.theme',
  GENERAL_UNIT = 'general.unit',

  // Layer visibility modes
  LAYER_CONNECTIONS_VISIBILITY_MODE = 'layer.connections.visibility_mode',
  LAYER_CONNECTION_BUBBLES_VISIBILITY_MODE = 'layer.connection_bubbles.visibility_mode',
  LAYER_FLOWS_VISIBILITY_MODE = 'layer.flows.visibility_mode',

  LAYER_WALLS_VISIBILITY_MODE = 'layer.walls.visibility_mode',
  LAYER_OBSTACLES_VISIBILITY_MODE = 'layer.obstacles.visibility_mode',
  LAYER_LOGICAL_SPACES_VISIBILITY_MODE = 'layer.logical_spaces.visibility_mode',
  LAYER_GROUPS_VISIBILITY_MODE = 'layer.groups.visibility_mode',

  LAYER_INTAKE_VISIBILITY_MODE = 'layer.intake.visibility_mode',
  LAYER_DELIVERY_VISIBILITY_MODE = 'layer.delivery.visibility_mode',
  LAYER_STORAGE_VISIBILITY_MODE = 'layer.storage.visibility_mode',
  LAYER_CHARGING_VISIBILITY_MODE = 'layer.charging.visibility_mode',
  LAYER_PARKING_VISIBILITY_MODE = 'layer.parking.visibility_mode',
  LAYER_MANUAL_VISIBILITY_MODE = 'layer.manual.visibility_mode',
  LAYER_INSERTION_VISIBILITY_MODE = 'layer.insertion.visibility_mode',
  LAYER_EXTRACTION_VISIBILITY_MODE = 'layer.extraction.visibility_mode',
  LAYER_PROCESS_VISIBILITY_MODE = 'layer.process.visibility_mode',

  LAYER_HIGHWAYS_VISIBILITY_MODE = 'layer.highways.visibility_mode',
  LAYER_ANGLED_HIGHWAYS_VISIBILITY_MODE = 'layer.angled_highways.visibility_mode',

  LAYER_REFERENCES_VISIBILITY_MODE = 'layer.references.visibility_mode',

  LAYER_AREA_NAMES_VISIBILITY_MODE = 'layer.names.area.visibility_mode',
  LAYER_HIGHWAY_NAMES_VISIBILITY_MODE = 'layer.names.highway.visibility_mode',
  LAYER_OBSTACLE_NAMES_VISIBILITY_MODE = 'layer.names.obstacle.visibility_mode',

  LAYER_GENERATED_POIS_VISIBILITY_MODE = 'layer.generated_pois.visibility_mode',
  LAYER_GENERATED_STATIONS_VISIBILITY_MODE = 'layer.generated_stations.visibility_mode',
  LAYER_GENERATED_TRACKS_VISIBILITY_MODE = 'layer.generated_tracks.visibility_mode',
  LAYER_GENERATED_SWEPT_PATHS_VISIBILITY_MODE = 'layer.generated_swept_paths.visibility_mode',
  LAYER_GENERATED_POI_CONNECTIONS_VISIBILITY_MODE = 'layer.generated_poi_connections.visibility_mode',

  // Nudge amount
  NUDGE_AMOUNT = 'nudge_amount',

  // Floorplan admin
  FLOORPLAN_ADMIN_SORT_BY = 'floorplanAdmin.sort_by',
  FLOORPLAN_ADMIN_SORT_ORDER = 'floorplanAdmin.sort_order',
  FLOORPLAN_ADMIN_VIEW_MODE = 'floorplanAdmin.view_mode',

  // Application
  APP_EXPERT_USER = 'app.expert_user',
  APP_STARTUP_MESSAGE_VERSION = 'app.startup_message_version',
  APP_HISTORY_DEPTH = 'app.history_depth',

  // Accordions: open/closed per Accordion, passed to it as persistId
  ACCORDION_POINT_CLOUD_PROPERTIES = 'ui.accordions.expanded.pointCloud.properties',
  ACCORDION_PROPERTY_PANEL_AREA_DETAILS = 'ui.accordions.expanded.propertyPanel.areaDetails',
  ACCORDION_PROPERTY_PANEL_DIMENSIONS = 'ui.accordions.expanded.propertyPanel.dimensions',
  ACCORDION_PROPERTY_PANEL_FLOW_STOPS = 'ui.accordions.expanded.propertyPanel.flowStops',
  ACCORDION_PROPERTY_PANEL_FLOWS = 'ui.accordions.expanded.propertyPanel.flows',
  ACCORDION_PROPERTY_PANEL_GROUPS = 'ui.accordions.expanded.propertyPanel.groups',
  ACCORDION_PROPERTY_PANEL_HIGHWAY = 'ui.accordions.expanded.propertyPanel.highway',
  ACCORDION_PROPERTY_PANEL_HIGHWAY_JIT_ROUTING_POINTS = 'ui.accordions.expanded.propertyPanel.highway.jitRoutingPoints',
  ACCORDION_PROPERTY_PANEL_HIGHWAY_VEHICLE_SETTINGS = 'ui.accordions.expanded.propertyPanel.highway.vehicleSettings',
  ACCORDION_PROPERTY_PANEL_LAYERS = 'ui.accordions.expanded.propertyPanel.layers',
  ACCORDION_PROPERTY_PANEL_LOGICAL_SPACE_TRAFFIC_RULES = 'ui.accordions.expanded.propertyPanel.logicalSpace.trafficRules',
  ACCORDION_PROPERTY_PANEL_MANUAL = 'ui.accordions.expanded.propertyPanel.manual',
  ACCORDION_PROPERTY_PANEL_MANUAL_LOAD_CARRIER_SETTINGS = 'ui.accordions.expanded.propertyPanel.manual.loadCarrierSettings',
  ACCORDION_PROPERTY_PANEL_MANUAL_VEHICLE_SETTINGS = 'ui.accordions.expanded.propertyPanel.manual.vehicleSettings',
  ACCORDION_PROPERTY_PANEL_OBSTACLE = 'ui.accordions.expanded.propertyPanel.obstacle',
  ACCORDION_PROPERTY_PANEL_RACKING = 'ui.accordions.expanded.propertyPanel.racking',
  ACCORDION_PROPERTY_PANEL_RACKING_LOAD_CARRIER_SETTINGS = 'ui.accordions.expanded.propertyPanel.racking.loadCarrierSettings',
  ACCORDION_PROPERTY_PANEL_RACKING_RACK_SPEC = 'ui.accordions.expanded.propertyPanel.racking.rackSpec',
  ACCORDION_PROPERTY_PANEL_RACKING_VEHICLE_SETTINGS = 'ui.accordions.expanded.propertyPanel.racking.vehicleSettings',
  ACCORDION_PROPERTY_PANEL_SELECTED_ITEMS = 'ui.accordions.expanded.propertyPanel.selectedItems',
  ACCORDION_PROPERTY_PANEL_SINGULAR_STATION = 'ui.accordions.expanded.propertyPanel.singularStation',
  ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_CONSTELLATION_SETTINGS = 'ui.accordions.expanded.propertyPanel.singularStation.constellationSettings',
  ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_LOAD_CARRIER_SETTINGS = 'ui.accordions.expanded.propertyPanel.singularStation.loadCarrierSettings',
  ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_VEHICLE_SETTINGS = 'ui.accordions.expanded.propertyPanel.singularStation.vehicleSettings',
  ACCORDION_PROPERTY_PANEL_STATIONARY = 'ui.accordions.expanded.propertyPanel.stationary',
  ACCORDION_PROPERTY_PANEL_STATIONARY_CONSTELLATION_SETTINGS = 'ui.accordions.expanded.propertyPanel.stationary.constellationSettings',
  ACCORDION_PROPERTY_PANEL_STATIONARY_VEHICLE_SETTINGS = 'ui.accordions.expanded.propertyPanel.stationary.vehicleSettings',
  ACCORDION_PROPERTY_PANEL_TEMPLATE_HIK = 'ui.accordions.expanded.propertyPanel.template.hik',
  ACCORDION_PROPERTY_PANEL_TEMPLATE_LASSIE = 'ui.accordions.expanded.propertyPanel.template.lassie',
  ACCORDION_PROPERTY_PANEL_TEMPLATE_LASSIE_MINI = 'ui.accordions.expanded.propertyPanel.template.lassieMini',
  ACCORDION_PROPERTY_PANEL_TEMPLATE_SHUTTLE_RACK = 'ui.accordions.expanded.propertyPanel.template.shuttleRack',
  ACCORDION_PROPERTY_PANEL_TEMPLATE_WORKSTATION_CONFIGS = 'ui.accordions.expanded.propertyPanel.template.workstationConfigs',
  ACCORDION_PROPERTY_PANEL_WALL = 'ui.accordions.expanded.propertyPanel.wall',
  ACCORDION_SIMULATION_EDIT_TIME = 'ui.accordions.expanded.simulation.edit.time',
  ACCORDION_SIMULATION_EDIT_VEHICLE_TYPES = 'ui.accordions.expanded.simulation.edit.vehicleTypes',
  ACCORDION_SIMULATION_EXPERIMENTAL_SETTINGS = 'ui.accordions.expanded.simulation.experimentalSettings',
  ACCORDION_SIMULATION_EXPERIMENTAL_SETTINGS_UVT_OVERRIDES = 'ui.accordions.expanded.simulation.experimentalSettings.uvtOverrides',
  ACCORDION_SIMULATION_GROUP_LIST = 'ui.accordions.expanded.simulation.groupList',
  ACCORDION_SIMULATION_LIST = 'ui.accordions.expanded.simulation.list',
  ACCORDION_VISUALIZATION_DEMO = 'ui.accordions.expanded.visualization.demo',

  // Level of detail
  LOD_TRACKS_ZOOM_THRESHOLD = 'lod.tracks_zoom_threshold',
  LOD_BOXES_ZOOM_THRESHOLD = 'lod.boxes_zoom_threshold',
  LOD_VEHICLE_LAYOUTS_ZOOM_THRESHOLD = 'lod.vehicle_layouts_zoom_threshold',
  LOD_BUBBLES_ZOOM_THRESHOLD = 'lod.bubbles_zoom_threshold',

  // Canvas
  CANVAS_SNAPPING_THRESHOLD = 'canvas.snapping_threshold',
  CANVAS_SHOW_FPS = 'canvas.show_fps',
}

export const DEFAULT_USER_PREFERENCES = {
  // General
  [UserPreferenceName.GENERAL_THEME]: Theme.LIGHT,
  [UserPreferenceName.GENERAL_UNIT]: Unit.MILLIMETERS,

  // Layer visibility modes
  [UserPreferenceName.LAYER_CONNECTIONS_VISIBILITY_MODE]: FORCE_HIDE,
  [UserPreferenceName.LAYER_CONNECTION_BUBBLES_VISIBILITY_MODE]: AUTO,
  [UserPreferenceName.LAYER_FLOWS_VISIBILITY_MODE]: FORCE_SHOW,

  [UserPreferenceName.LAYER_WALLS_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_OBSTACLES_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_LOGICAL_SPACES_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_GROUPS_VISIBILITY_MODE]: FORCE_SHOW,

  [UserPreferenceName.LAYER_INTAKE_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_DELIVERY_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_STORAGE_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_CHARGING_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_PARKING_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_MANUAL_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_INSERTION_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_EXTRACTION_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_PROCESS_VISIBILITY_MODE]: FORCE_SHOW,

  [UserPreferenceName.LAYER_HIGHWAYS_VISIBILITY_MODE]: FORCE_SHOW,
  [UserPreferenceName.LAYER_ANGLED_HIGHWAYS_VISIBILITY_MODE]: FORCE_SHOW,

  [UserPreferenceName.LAYER_REFERENCES_VISIBILITY_MODE]: FORCE_SHOW,

  [UserPreferenceName.LAYER_AREA_NAMES_VISIBILITY_MODE]: FORCE_HIDE,
  [UserPreferenceName.LAYER_HIGHWAY_NAMES_VISIBILITY_MODE]: FORCE_HIDE,
  [UserPreferenceName.LAYER_OBSTACLE_NAMES_VISIBILITY_MODE]: FORCE_HIDE,

  [UserPreferenceName.LAYER_GENERATED_POIS_VISIBILITY_MODE]: AUTO,
  [UserPreferenceName.LAYER_GENERATED_STATIONS_VISIBILITY_MODE]: AUTO,
  [UserPreferenceName.LAYER_GENERATED_TRACKS_VISIBILITY_MODE]: AUTO,
  [UserPreferenceName.LAYER_GENERATED_SWEPT_PATHS_VISIBILITY_MODE]: AUTO,
  [UserPreferenceName.LAYER_GENERATED_POI_CONNECTIONS_VISIBILITY_MODE]: AUTO,

  // Nudge amount
  [UserPreferenceName.NUDGE_AMOUNT]: 10,

  // Floorplan admin
  [UserPreferenceName.FLOORPLAN_ADMIN_VIEW_MODE]: FloorplanViewMode.GRID,
  [UserPreferenceName.FLOORPLAN_ADMIN_SORT_BY]: DEFAULT_SORTING_CRITERIA,
  [UserPreferenceName.FLOORPLAN_ADMIN_SORT_ORDER]:
    DEFAULT_SORT_ORDER_OPTIONS[DEFAULT_SORTING_CRITERIA],

  // Application
  [UserPreferenceName.APP_EXPERT_USER]: false,
  [UserPreferenceName.APP_STARTUP_MESSAGE_VERSION]: '1.0.0',
  [UserPreferenceName.APP_HISTORY_DEPTH]: 12,

  // Accordions: open/closed per Accordion
  [UserPreferenceName.ACCORDION_POINT_CLOUD_PROPERTIES]: false,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_AREA_DETAILS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_DIMENSIONS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_FLOW_STOPS]: false,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_FLOWS]: false,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_GROUPS]: false,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_HIGHWAY]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_HIGHWAY_JIT_ROUTING_POINTS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_HIGHWAY_VEHICLE_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_LAYERS]: false,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_LOGICAL_SPACE_TRAFFIC_RULES]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_MANUAL]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_MANUAL_LOAD_CARRIER_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_MANUAL_VEHICLE_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_OBSTACLE]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_RACKING]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_RACKING_LOAD_CARRIER_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_RACKING_RACK_SPEC]: false,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_RACKING_VEHICLE_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_SELECTED_ITEMS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_SINGULAR_STATION]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_CONSTELLATION_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_LOAD_CARRIER_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_VEHICLE_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_STATIONARY]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_STATIONARY_CONSTELLATION_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_STATIONARY_VEHICLE_SETTINGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_HIK]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_LASSIE]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_LASSIE_MINI]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_SHUTTLE_RACK]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_WORKSTATION_CONFIGS]: true,
  [UserPreferenceName.ACCORDION_PROPERTY_PANEL_WALL]: true,
  [UserPreferenceName.ACCORDION_SIMULATION_EDIT_TIME]: true,
  [UserPreferenceName.ACCORDION_SIMULATION_EDIT_VEHICLE_TYPES]: false,
  [UserPreferenceName.ACCORDION_SIMULATION_EXPERIMENTAL_SETTINGS]: false,
  [UserPreferenceName.ACCORDION_SIMULATION_EXPERIMENTAL_SETTINGS_UVT_OVERRIDES]: false,
  [UserPreferenceName.ACCORDION_SIMULATION_GROUP_LIST]: true,
  [UserPreferenceName.ACCORDION_SIMULATION_LIST]: true,
  [UserPreferenceName.ACCORDION_VISUALIZATION_DEMO]: false,

  // Level of detail
  [UserPreferenceName.LOD_BUBBLES_ZOOM_THRESHOLD]: DEFAULT_BUBBLES_ZOOM_THRESHOLD,
  [UserPreferenceName.LOD_BOXES_ZOOM_THRESHOLD]: DEFAULT_BOXES_ZOOM_THRESHOLD,
  [UserPreferenceName.LOD_VEHICLE_LAYOUTS_ZOOM_THRESHOLD]: DEFAULT_VEHICLE_LAYOUTS_ZOOM_THRESHOLD,
  [UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD]: DEFAULT_TRACKS_ZOOM_THRESHOLD,

  // Canvas
  [UserPreferenceName.CANVAS_SNAPPING_THRESHOLD]: 1000,
  [UserPreferenceName.CANVAS_SHOW_FPS]: false,
};
